/**
 * Plugin Iframe Component
 *
 * Renders a sandboxed iframe for a plugin UI extension.
 */

import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { PluginBridge, createPluginBridge } from './PluginBridge';
import { pluginHotReloadClient } from './hot-reload';

export interface PluginIframeProps {
  pluginId: string;
  bundleUrl: string;
  initialProps?: Record<string, any>;
  sandbox?: string;
  allowedOrigin?: string;
  minHeight?: number;
  maxHeight?: number;
  onReady?: () => void;
  onError?: (error: string) => void;
  onResize?: (width: number, height: number) => void;
  className?: string;
  style?: React.CSSProperties;
}

export interface PluginIframeHandle {
  sendEvent: (eventType: string, payload: any) => void;
  updateProps: (props: Record<string, any>) => void;
  getBridge: () => PluginBridge | null;
}

const DEFAULT_SANDBOX =
  'allow-scripts allow-forms allow-popups allow-same-origin allow-modals';

export const PluginIframe = forwardRef<PluginIframeHandle, PluginIframeProps>(
  (
    {
      pluginId,
      bundleUrl,
      initialProps,
      sandbox = DEFAULT_SANDBOX,
      allowedOrigin = '*',
      minHeight = 100,
      maxHeight = 2000,
      onReady,
      onError,
      onResize,
      className,
      style,
    },
    ref,
  ) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const bridgeRef = useRef<PluginBridge | null>(null);
    const [height, setHeight] = useState(minHeight);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resolvedAllowedOrigin = useMemo(() => {
      if (allowedOrigin !== '*') return allowedOrigin;
      try {
        const parsed = new URL(bundleUrl, window.location.origin);
        return parsed.origin;
      } catch {
        return window.location.origin;
      }
    }, [allowedOrigin, bundleUrl]);

    // Handle resize from plugin
    const handleResize = useCallback(
      (width: number, newHeight: number) => {
        const clampedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
        setHeight(clampedHeight);
        onResize?.(width, clampedHeight);
      },
      [minHeight, maxHeight, onResize],
    );

    // Handle ready event
    const handleReady = useCallback(() => {
      setReady(true);
      setError(null);

      // Send initial props if provided
      if (initialProps && bridgeRef.current) {
        bridgeRef.current.updateProps(initialProps);
      }

      onReady?.();
    }, [initialProps, onReady]);

    // Handle error
    const handleError = useCallback(
      (errorMessage: string) => {
        setError(errorMessage);
        onError?.(errorMessage);
      },
      [onError],
    );

    // Set up bridge when iframe loads
    useEffect(() => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      const bridge = createPluginBridge({
        pluginId,
        iframe,
        allowedOrigin: resolvedAllowedOrigin,
        onReady: handleReady,
        onError: handleError,
        onResize: handleResize,
      });

      bridgeRef.current = bridge;

      // Wave 18c: Register bridge for hot-reload iframe notification
      pluginHotReloadClient.registerBridge(pluginId, bridge);

      return () => {
        bridge.destroy();
        bridgeRef.current = null;
        pluginHotReloadClient.unregisterBridge(pluginId);
      };
    }, [pluginId, resolvedAllowedOrigin, handleReady, handleError, handleResize]);

    // Expose imperative handle
    useImperativeHandle(
      ref,
      () => ({
        sendEvent: (eventType: string, payload: any) => {
          bridgeRef.current?.sendEvent(eventType, payload);
        },
        updateProps: (props: Record<string, any>) => {
          bridgeRef.current?.updateProps(props);
        },
        getBridge: () => bridgeRef.current,
      }),
      [],
    );

    return (
      <div
        className={className}
        style={{
          position: 'relative',
          width: '100%',
          ...style,
        }}
      >
        {error && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              padding: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              borderRadius: '4px',
              fontSize: '14px',
              zIndex: 10,
            }}
          >
            Plugin Error: {error}
          </div>
        )}

        {!ready && !error && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: minHeight,
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '3px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={bundleUrl}
          sandbox={sandbox}
          style={{
            width: '100%',
            height: `${height}px`,
            border: 'none',
            display: ready ? 'block' : 'none',
          }}
          title={`Plugin: ${pluginId}`}
        />
      </div>
    );
  },
);

PluginIframe.displayName = 'PluginIframe';

export default PluginIframe;
